import axiosInstance from "@/lib/axiosInstance";
import type { CustomAxiosRequestConfig } from "@/types/common.types";
import type { EffectiveAccess, EffectiveAccessResponse } from "./access.types";

/**
 * Another person's access as the engine works it out at one login (`servengine` or a company's
 * tenant id): the companies they are in, the roles that apply there and the permissions those add
 * up to. It is the engine's own answer to "which entities does this role reach", so nothing here
 * re-derives the level and home-company rules (backend `engine.appliesIn`).
 */
export const fetchUserEffectiveAccess = async (
  userId: string,
  login: string,
  signal?: AbortSignal,
): Promise<EffectiveAccess> => {
  const { data } = await axiosInstance.get<EffectiveAccessResponse>(
    `/access/users/${userId}/effective`,
    {
      params: { login },
      signal,
      suppressErrorAlert: true,
    } as CustomAxiosRequestConfig,
  );
  return data.data;
};

/**
 * The ServEngine read with each company replaced by that company's own read. An entity-level role
 * applies only at its company's login, so the ServEngine read leaves it out (D253: PC Jr reads
 * empty there and `pc_jr` with 72 switches at RP One). A company with no read of its own keeps
 * what the ServEngine read says.
 */
export const withCompanyReads = (
  servengine: EffectiveAccess,
  companyReads: Array<{ tenantId: string; access: EffectiveAccess | null }>,
): EffectiveAccess => {
  const companies = { ...servengine.companies };
  for (const { tenantId, access } of companyReads) {
    const company = access?.companies[tenantId];
    if (company) companies[tenantId] = company;
  }
  return { ...servengine, companies };
};
